/**
 * Line-budget windowing over items of heterogeneous height.
 *
 * WINDOW BY RENDERED LINES, NEVER BY ROW COUNT. Row-count windowing is what
 * produced EQStack's height-0 overpaint bug family (#99/#101/#103): a
 * yoga-shrunk box paints its text over the next row, and the diagnosis cost
 * hexdump archaeology. The caller supplies a pure `heightOf(index)` because
 * only the caller knows how its content wraps.
 *
 * Predicted heights, not measured ones. `ink-scroll-view` (the widely-used
 * alternative) measures by rendering into a virtual DOM — correct for short
 * lists with unpredictable content, unavailable when the window is what decides
 * WHAT to render and the list is a 100k-message thread behind bounded-memory
 * eviction.
 */
import { finiteOr, isPositive } from "./finite.js";
/**
 * Pick the anchor. `cursor < 0`, or within `nearEnd` of the tail, means the
 * list should sit against its bottom edge.
 *
 * The sentinel and the anchor unify: follow-tail IS end-anchoring.
 */
export function chooseAnchor(cursor, itemCount, nearEnd = 2) {
    if (cursor < 0)
        return "end";
    if (itemCount <= 0)
        return "end";
    return cursor >= itemCount - nearEnd ? "end" : "cursor";
}
export function lineWindow(spec) {
    const itemCount = Math.floor(finiteOr(spec.itemCount, 0));
    const budgetLines = spec.budgetLines;
    if (!isPositive(itemCount))
        return { start: 0, end: 0, usedLines: 0 };
    // Per-invocation memo. The walk visits some indices twice (up, then down,
    // then backfill up) and the item array cannot change mid-call, so this is
    // free and correct — and it means no consumer has to reach for `useCallback`
    // to make its estimator cheap. Cross-render memoisation stays theirs.
    const memo = new Map();
    const h = (i) => {
        const cached = memo.get(i);
        if (cached !== undefined)
            return cached;
        // A NaN height poisons `used`, after which every break condition is false
        // and the walk never terminates. Treated as 0 rather than propagated: the
        // `used > 0` admission clause below already copes with a zero-height item,
        // so a broken estimator costs a mis-sized row instead of the whole list.
        const measured = Math.max(0, Math.floor(finiteOr(spec.heightOf(i), 0)));
        memo.set(i, measured);
        return measured;
    };
    const requested = Math.floor(finiteOr(spec.cursor, -1));
    const followTail = requested < 0;
    const cursor = followTail ? itemCount - 1 : Math.min(Math.max(requested, 0), itemCount - 1);
    const anchor = followTail ? "end" : (spec.anchor ?? "cursor");
    // Documented degenerate case: no budget still yields the cursor's row, so the
    // "cursor is always visible" invariant holds during a resize transient rather
    // than blinking to empty.
    // POSITIVE predicate, deliberately. `budgetLines <= 0` is false for NaN, so
    // that spelling admitted it and every subsequent break condition became
    // false — see `finite.ts` for the incident. This form treats a non-finite
    // budget as no budget, which fails CLOSED to the cursor's own row.
    if (!isPositive(budgetLines))
        return { start: cursor, end: cursor + 1, usedLines: h(cursor) };
    if (anchor === "end") {
        let start = itemCount;
        let used = 0;
        while (start > 0) {
            const next = h(start - 1);
            // `used > 0` is what admits a single over-tall tail item.
            if (used + next > budgetLines && used > 0)
                break;
            used += next;
            start -= 1;
        }
        // The cursor may sit above an end-anchored window (that is the point of the
        // anchor). It must still be inside it.
        while (start > cursor) {
            start -= 1;
            used += h(start);
        }
        return { start, end: itemCount, usedLines: used };
    }
    // Cursor-anchored: seed with the cursor itself, unconditionally, so an item
    // taller than the budget is shown rather than dropped.
    let start = cursor;
    let end = cursor + 1;
    let used = h(cursor);
    const aboveBudget = Math.floor(budgetLines * finiteOr(spec.aboveFraction ?? 0.4, 0.4));
    let aboveUsed = 0;
    while (start > 0) {
        const next = h(start - 1);
        if (aboveUsed + next > aboveBudget)
            break;
        if (used + next > budgetLines)
            break;
        start -= 1;
        used += next;
        aboveUsed += next;
    }
    while (end < itemCount) {
        const next = h(end);
        if (used + next > budgetLines)
            break;
        used += next;
        end += 1;
    }
    // Backfill upward with whatever the downward pass did not spend.
    while (start > 0) {
        const next = h(start - 1);
        if (used + next > budgetLines)
            break;
        start -= 1;
        used += next;
    }
    return { start, end, usedLines: used };
}
//# sourceMappingURL=line-window.js.map